import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';
import { LeadItem } from '@/features/marketing/components/LeadMagnetTab';

interface UseLeadMagnetProps {
  enabled: boolean;
  userId?: string;
}

/**
 * Hook to manage lead magnet tab state and operations
 */
export const useLeadMagnet = ({ enabled, userId }: UseLeadMagnetProps) => {
  const [leads, setLeads] = useState<LeadItem[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsError, setLeadsError] = useState<string | null>(null);
  const [leadPageIndex, setLeadPageIndex] = useState(0);
  const [leadPageSize, setLeadPageSize] = useState(10);
  const [leadTotal, setLeadTotal] = useState(0);
  const [leadSearch, setLeadSearch] = useState('');

  const fetchLeads = useCallback(
    async (pageIndex = 0, pageSize = 10) => {
      if (!enabled) return;

      try {
        setLeadsLoading(true);
        setLeadsError(null);

        const page = pageIndex + 1;
        const limit = pageSize;

        const res = await api.get<{
          data: LeadItem[];
          total: number;
          page: number;
          limit: number;
          totalPages: number;
        }>('/mxm/leads/all', {
          params: { page, limit, userId },
        });

        setLeads(res.data.data || []);
        setLeadTotal(res.data.total ?? 0);
        setLeadPageIndex((res.data.page || 1) - 1);
        setLeadPageSize(res.data.limit || pageSize);
      } catch (err: unknown) {
        const msg = getErrorMessage(err);
        setLeadsError(Array.isArray(msg) ? msg.join(', ') : msg);
      } finally {
        setLeadsLoading(false);
      }
    },
    [enabled, userId],
  );

  // Fetch leads when pagination changes
  useEffect(() => {
    if (enabled) {
      fetchLeads(leadPageIndex, leadPageSize);
    }
  }, [enabled, leadPageIndex, leadPageSize, fetchLeads]);

  const filteredLeads = useMemo(
    () =>
      leadSearch.trim()
        ? leads.filter((lead) =>
          lead.email?.toLowerCase().includes(leadSearch.toLowerCase()),
        )
        : leads,
    [leads, leadSearch],
  );

  const handleLeadSearch = useCallback(async (query: string) => {
    setLeadSearch(query);
  }, []);

  return {
    leads: filteredLeads,
    leadsLoading,
    leadsError,
    pagination: {
      pageIndex: leadPageIndex,
      pageSize: leadPageSize,
      totalCount: leadTotal,
    },
    leadSearch,
    handleLeadSearch,
    handlePaginationChange: ({ pageIndex, pageSize }: { pageIndex: number; pageSize: number }) => {
      setLeadPageIndex(pageIndex);
      setLeadPageSize(pageSize);
    },
    refresh: () => fetchLeads(leadPageIndex, leadPageSize),
  };
};

